import JsPDF from 'jspdf';

import { convertArrayToMatrix, convertStringArrayToSizedMatrix } from '../../../../helpers/array';
import { formatDate, isFutureDate } from '../../../../helpers/date';
import { generateDownloadFileName } from '../../../../helpers/download';
import { sortByStartDateAsc } from '../../../../helpers/sort';
import { capitalizeFirstCharacter, convertHtmlParagraphStringToArray } from '../../../../helpers/string';
import trans from '../../../../helpers/trans';
import { Candidate } from '../../../../models/Candidate/Candidate';
import { Competency } from '../../../../models/Competencies/Competencies';
import { JobExperience } from '../../../../models/JobExperience/JobExperience';
import { SchoolExperience } from '../../../../models/SchoolExperience/SchoolExperience';
import { SkillsPassport } from '../../../../models/SkillsPassport/SkillsPassport';
import { CompetencyLevel } from '../../../../types';
import { getEducationLevelDescription } from '../../SchoolExperiencesCard/helpers';

interface TextOptions {
    marginTop?: number;
    marginLeft?: number;
    splitSize?: number;
    fontStyle?: string;
    fontSize?: number;
    lineHeight?: number;
    color?: {
        r: number;
        g: number;
        b: number;
    };
    prefix?: string;
}

interface ImageDimensions {
    width: number;
    height: number;
}

interface ImageOptions {
    type: string;
    size: ImageDimensions;
    x?: number;
    y?: number;
}

const pdfImage = (size: number, ratio: ImageDimensions): ImageDimensions => ({
    width: size,
    height: size * (ratio.height / ratio.width),
});

const colors = {
    primary: { r: 1, g: 188, b: 156 },
    secondary: { r: 17, g: 204, b: 204 },
    white: { r: 255, g: 255, b: 255 },
    black: { r: 33, g: 43, b: 54 },
    greyLight: { r: 230, g: 232, b: 234 },
    grey: { r: 162, g: 169, b: 176 },
    greyDark: { r: 95, g: 105, b: 116 },
};

export const downloadSkillsPassport = (candidate: Candidate, skillsPassport: SkillsPassport): Promise<void> => {
    const {
        personalityDescription,
        competencies,
        learningStyle,
        communicationStyles,
        talents,
        workplacePreferences,
        skills,
        jobExperiences,
        schoolExperiences,
    } = skillsPassport;

    const fileName = generateDownloadFileName({
        subject: candidate?.fullName || '',
        content: trans('pdf.skillsPassport.fileNameContent'),
        extension: 'pdf',
    });

    const hasCompletedTmaAnalysis = [
        personalityDescription,
        communicationStyles.effective.length > 0,
        communicationStyles.ineffective.length > 0,
        !!competencies,
        learningStyle.styles.length > 0,
        talents.length > 0,
        workplacePreferences.length > 0,
    ].some(Boolean);

    const personalityDescriptionParagraphs = convertHtmlParagraphStringToArray(personalityDescription);
    const personalityDescriptionPages = convertStringArrayToSizedMatrix(personalityDescriptionParagraphs, 3800);

    const effectiveCommunicationStylesPages = convertStringArrayToSizedMatrix(communicationStyles.effective, 3800);
    const ineffectiveCommunicationStylesPages = convertStringArrayToSizedMatrix(communicationStyles.ineffective, 3800);

    const talentList = talents
        .filter(talent => talent.value <= 3 || talent.value >= 7)
        .map(talent => talent.name);
    const talentMatrix: string[][] = convertArrayToMatrix<string>(talentList, 22);

    const skillList = skills.map(skill => capitalizeFirstCharacter(skill.name));
    const skillMatrix: string[][] = convertArrayToMatrix<string>(skillList, 24);

    const highCompetencies = competencies
        ? competencies[CompetencyLevel.high]
        : [];

    const regularCompetencies = competencies
        ? competencies[CompetencyLevel.regular]
        : [];

    const competencyList = [
        ...highCompetencies,
        ...regularCompetencies,
    ];
    const competencyMatrix: Competency[][] = convertArrayToMatrix<Competency>(competencyList, 24);

    const sortedSchoolExperiences = [...schoolExperiences].sort(sortByStartDateAsc);
    const schoolExperienceMatrix: SchoolExperience[][] = convertArrayToMatrix<SchoolExperience>(sortedSchoolExperiences, 6);

    const sortedJobExperiences = [...jobExperiences].sort(sortByStartDateAsc);
    const jobExperienceMatrix: JobExperience[][] = convertArrayToMatrix<JobExperience>(sortedJobExperiences, 5);

    const learningStylePages = convertStringArrayToSizedMatrix(learningStyle.styles, 3000);

    const workplacePreferencesPages = convertStringArrayToSizedMatrix(workplacePreferences, 2000);

    // Logo
    // eslint-disable-next-line max-len
    const logo = 'data:image/jpeg;base64,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';
    const logoDimensions = pdfImage(60, { width: 320, height: 78 });

    // Document config
    const doc = new JsPDF({
        format: 'a4',
        orientation: 'p',
        unit: 'mm',
    });

    let currentYPos = 1;

    // Functions
    const text = (value: string, options: TextOptions): void => {
        const {
            marginTop = 0,
            marginLeft = 10,
            splitSize = 240,
            fontStyle = '',
            fontSize = 12,
            lineHeight = 7,
            color = colors.black,
            prefix = '',
        } = options;

        const prefixedValue = prefix ? (prefix + value) : value;
        const splitText = doc.splitTextToSize(prefixedValue, splitSize);

        const xPos = marginLeft;
        const yPos = currentYPos + marginTop;

        doc.setFont('Helvetica', fontStyle);
        doc.setFontSize(fontSize);
        doc.setTextColor(color.r, color.g, color.b);

        doc.text(splitText, xPos, yPos);

        currentYPos += (splitText.length * lineHeight) + marginTop;
    };

    const heading = (value: string, options?: TextOptions): void => text(value, {
        splitSize: 200,
        fontStyle: 'bold',
        color: colors.primary,
        ...options,
    });

    const h1 = (value: string, options?: TextOptions): void => heading(value, {
        marginTop: 15,
        fontSize: 16,
        ...options,
    });

    const h2 = (value: string, options?: TextOptions): void => heading(value, {
        fontSize: 14,
        ...options,
    });

    const h3 = (value: string, options?: TextOptions): void => heading(value, {
        color: colors.greyDark,
        ...options,
    });

    const p = (value: string, options?: TextOptions): void => text(value, {
        ...options,
    });

    const li = (value: string, options?: TextOptions): void => text(value, {
        marginLeft: 15,
        prefix: '• ',
        ...options,
    });

    const img = (dataString: string, options: ImageOptions): void => {
        const {
            type,
            size,
            x = 0,
            y = 0,
        } = options;

        doc.addImage(dataString, type, x, y, size.width, size.height);
    };

    const line = (): void => {
        doc.line(10, currentYPos, 200, currentYPos);
    };

    const about = (): void => {
        const schoolExperienceSummary = trans('pdf.skillsPassport.about.schoolExperiences', {
            firstName: candidate?.firstName,
            amount: String(schoolExperiences.length),
        });

        const jobExperienceSummary = trans('pdf.skillsPassport.about.jobExperiences', {
            firstName: candidate?.firstName,
            amount: String(jobExperiences.length),
        });

        const tmaSummary = trans('pdf.skillsPassport.about.tma', {
            firstName: candidate?.firstName,
            competencyAmount: String(competencyList.length),
            talentAmount: String(talentList.length),
            learningStyleAmount: String(learningStyle.styles.length),
        });

        const noTma = trans('pdf.skillsPassport.about.noTma', {
            firstName: candidate?.firstName,
        });

        line();
        h2(trans('pdf.skillsPassport.about.title'), { marginTop: 20 });

        p(schoolExperienceSummary);
        p(jobExperienceSummary);

        p(hasCompletedTmaAnalysis ? tmaSummary : noTma, { splitSize: 180 });
    };

    const schoolExperience = (item: SchoolExperience, marginTop?: number): void => {
        const school = trans('pdf.skillsPassport.schoolExperience.item.atSchool', {
            school: item.school,
        });

        const dates = trans('pdf.skillsPassport.schoolExperience.item.dates', {
            startDate: formatDate(item.startDate, 'MMMM yyyy'),
            endDate: isFutureDate(item.endDate)
                ? trans('common.presentTime')
                : formatDate(item.endDate, 'MMMM yyyy'),
        });

        line();
        h2(item.fieldOfStudy.toUpperCase(), { marginTop });
        h3(school);
        p(getEducationLevelDescription(item.educationLevel, item.learningPath));
        p(dates, { color: colors.grey });
    };

    const jobExperience = (item: JobExperience, marginTop?: number): void => {
        const company = trans('pdf.skillsPassport.jobExperience.item.atCompany', {
            company: item.company,
        });

        const dates = trans('pdf.skillsPassport.jobExperience.item.dates', {
            startDate: formatDate(item.startDate, 'MMMM yyyy'),
            endDate: item.endDate
                ? formatDate(item.endDate, 'MMMM yyyy')
                : trans('common.presentTime'),
        });

        line();
        h2(item.position.toUpperCase(), { marginTop });
        h3(company);
        p(dates, { color: colors.grey });

        p(item.description, { splitSize: 180 });
    };

    const noResults = (value: string): void => {
        line();
        p(value, { splitSize: 180, marginTop: 10 });
    };

    const newPage = (): void => {
        doc.addPage();
        currentYPos = 0;
    };

    /* Introduction */
    img(logo, {
        type: 'jpeg',
        size: logoDimensions,
        x: 10,
        y: 10,
    });

    h1(trans('pdf.skillsPassport.title', { fullName: candidate?.fullName }), {
        marginTop: 40,
    });
    p(trans('pdf.skillsPassport.downloadDate', { date: formatDate(new Date()) }));

    about();

    newPage();

    /* Personality description */
    h1(trans('pdf.skillsPassport.personalityDescription.title'));
    p(trans('pdf.skillsPassport.personalityDescription.description', {
        firstName: candidate?.firstName,
    }));

    line();

    if (!personalityDescription) {
        noResults(trans('pdf.skillsPassport.personalityDescription.noResults'));
    }

    for (let pageIndex = 0; pageIndex < personalityDescriptionPages.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < personalityDescriptionPages[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 8;

            p(personalityDescriptionPages[pageIndex][itemIndex], {
                marginTop,
                splitSize: 180,
                lineHeight: 5,
            });
        }

        if (pageIndex < personalityDescriptionPages.length - 1) newPage();
    }

    newPage();

    /* Communication styles */
    h1(trans('pdf.skillsPassport.communicationStyles.title'));
    p(trans('pdf.skillsPassport.communicationStyles.description', {
        firstName: candidate?.firstName,
    }));

    line();

    if (communicationStyles.effective.length === 0 || communicationStyles.ineffective.length === 0) {
        noResults(trans('pdf.skillsPassport.communicationStyles.noResults'));
    } else {
        h2(trans('pdf.skillsPassport.communicationStyles.effectiveTitle'), {
            marginTop: 15,
            splitSize: 180,
        });

        for (let pageIndex = 0; pageIndex < effectiveCommunicationStylesPages.length; pageIndex += 1) {
            for (let itemIndex = 0; itemIndex < effectiveCommunicationStylesPages[pageIndex].length; itemIndex += 1) {
                const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 5;

                p(effectiveCommunicationStylesPages[pageIndex][itemIndex], {
                    marginTop,
                    splitSize: 180,
                    lineHeight: 5,
                });
            }

            if (pageIndex < effectiveCommunicationStylesPages.length - 1) newPage();
        }

        newPage();

        h2(trans('pdf.skillsPassport.communicationStyles.ineffectiveTitle'), {
            marginTop: 15,
            splitSize: 180,
        });

        for (let pageIndex = 0; pageIndex < ineffectiveCommunicationStylesPages.length; pageIndex += 1) {
            for (let itemIndex = 0; itemIndex < ineffectiveCommunicationStylesPages[pageIndex].length; itemIndex += 1) {
                const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 5;

                p(ineffectiveCommunicationStylesPages[pageIndex][itemIndex], {
                    marginTop,
                    splitSize: 180,
                    lineHeight: 5,
                });
            }

            if (pageIndex < ineffectiveCommunicationStylesPages.length - 1) newPage();
        }
    }

    newPage();

    /* Talents */
    h1(trans('pdf.skillsPassport.talents.title'));
    p(trans('pdf.skillsPassport.talents.description', {
        firstName: candidate?.firstName,
    }));

    if (talentList.length === 0) {
        noResults(trans('pdf.skillsPassport.talents.noResults'));
    }

    for (let pageIndex = 0; pageIndex < talentMatrix.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < talentMatrix[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 3;

            li(talentMatrix[pageIndex][itemIndex], { marginTop });
        }

        if (pageIndex < talentMatrix.length - 1) newPage();
    }

    newPage();

    /* Skills */
    h1(trans('pdf.skillsPassport.skills.title'));
    p(trans('pdf.skillsPassport.skills.description', {
        firstName: candidate?.firstName,
    }));

    if (skillList.length === 0) {
        noResults(trans('pdf.skillsPassport.skills.noResults'));
    }

    for (let pageIndex = 0; pageIndex < skillMatrix.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < skillMatrix[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 3;

            li(skillMatrix[pageIndex][itemIndex], { marginTop });
        }

        if (pageIndex < skillMatrix.length - 1) newPage();
    }

    newPage();

    /* Competencies */
    h1(trans('pdf.skillsPassport.competencies.title'));
    p(trans('pdf.skillsPassport.competencies.description', {
        firstName: candidate?.firstName,
    }));

    if (competencyList.length === 0) {
        noResults(trans('pdf.skillsPassport.competencies.noResults'));
    }

    for (let pageIndex = 0; pageIndex < competencyMatrix.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < competencyMatrix[pageIndex].length; itemIndex += 1) {
            const competency = competencyMatrix[pageIndex][itemIndex];
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 3;
            const levelTranslation = trans(`containers.competenciesCard.competencyLevel.label.${competency.level}`);

            li(`${competency.label} (${levelTranslation})`, { marginTop });
        }

        if (pageIndex < competencyMatrix.length - 1) newPage();
    }

    newPage();

    /* School experience */
    h1(trans('pdf.skillsPassport.schoolExperience.title'));
    p(trans('pdf.skillsPassport.schoolExperience.description', {
        firstName: candidate?.firstName,
    }));

    if (schoolExperiences.length === 0) {
        noResults(trans('pdf.skillsPassport.schoolExperience.noResults'));
    }

    for (let pageIndex = 0; pageIndex < schoolExperienceMatrix.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < schoolExperienceMatrix[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 8;

            schoolExperience(schoolExperienceMatrix[pageIndex][itemIndex], marginTop);
        }

        if (pageIndex < schoolExperienceMatrix.length - 1) newPage();
    }

    newPage();

    /* Job experience */
    h1(trans('pdf.skillsPassport.jobExperience.title'));
    p(trans('pdf.skillsPassport.jobExperience.description', {
        firstName: candidate?.firstName,
    }));

    if (jobExperiences.length === 0) {
        noResults(trans('pdf.skillsPassport.jobExperience.noResults'));
    }

    for (let pageIndex = 0; pageIndex < jobExperienceMatrix.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < jobExperienceMatrix[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 8;

            jobExperience(jobExperienceMatrix[pageIndex][itemIndex], marginTop);
        }

        if (pageIndex < jobExperienceMatrix.length - 1) newPage();
    }

    newPage();

    /* Learning style */
    h1(trans('pdf.skillsPassport.learningStyle.title'));
    p(trans('pdf.skillsPassport.learningStyle.description', {
        firstName: candidate?.firstName,
    }));

    line();

    if (learningStyle.styles.length === 0) {
        noResults(trans('pdf.skillsPassport.learningStyle.noResults'));
    }

    for (let pageIndex = 0; pageIndex < learningStylePages.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < learningStylePages[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 8;

            p(learningStylePages[pageIndex][itemIndex], {
                marginTop,
                splitSize: 180,
                lineHeight: 5,
            });
        }

        if (pageIndex < learningStylePages.length - 1) newPage();
    }

    newPage();

    /* Workplace preferences */
    h1(trans('pdf.skillsPassport.workplacePreferences.title'));
    p(trans('pdf.skillsPassport.workplacePreferences.description', {
        firstName: candidate?.firstName,
    }));

    line();

    if (workplacePreferences.length === 0) {
        noResults(trans('pdf.skillsPassport.workplacePreferences.noResults'));
    }

    for (let pageIndex = 0; pageIndex < workplacePreferencesPages.length; pageIndex += 1) {
        for (let itemIndex = 0; itemIndex < workplacePreferencesPages[pageIndex].length; itemIndex += 1) {
            const marginTop = pageIndex !== 0 && itemIndex === 0 ? 15 : 8;

            p(workplacePreferencesPages[pageIndex][itemIndex], {
                marginTop,
                splitSize: 180,
                lineHeight: 5,
            });
        }

        if (pageIndex < workplacePreferencesPages.length - 1) newPage();
    }

    return doc.save(fileName, { returnPromise: true }) as unknown as Promise<void>;
};
